import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    Check,
    X,
    Search,
    ChevronRight,
    User,
    Loader2,
    Filter,
    Users
} from 'lucide-react';
import Toast from '../Common/Toast';
import ConfirmDialog from '../Common/ConfirmDialog';
import './EscalaoManager.css';
import API_BASE_URL from '../../config/api';

const EscalaoManager = () => {
    const [escalaos, setEscalaos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [sports, setSports] = useState([]);
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState({ isOpen: false, id: null, name: '' });

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingEscalao, setEditingEscalao] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        minAge: '',
        maxAge: '',
        sportIds: []
    });

    // Search & filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSportId, setFilterSportId] = useState('');

    const addToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    useEffect(() => {
        fetchEscalaos();
        fetchSports();
    }, []);

    const fetchSports = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/sports`);
            if (res.ok) {
                const data = await res.json();
                setSports(data);
            }
        } catch (err) {
            addToast('Erro ao carregar modalidades', 'error');
        }
    };

    const fetchEscalaos = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/escalaos/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setEscalaos(data);
            }
        } catch (err) {
            addToast('Erro ao carregar escalões', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const url = editingEscalao
                ? `${API_BASE_URL}/api/escalaos/${editingEscalao.id}`
                : `${API_BASE_URL}/api/escalaos`;

            const method = editingEscalao ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    minAge: formData.minAge ? parseInt(formData.minAge) : null,
                    maxAge: formData.maxAge ? parseInt(formData.maxAge) : null,
                    sportIds: formData.sportIds
                })
            });

            if (res.ok) {
                addToast(editingEscalao ? 'Escalão atualizado com sucesso!' : 'Escalão criado com sucesso!');
                closeModal();
                await fetchEscalaos();
            } else {
                const err = await res.json();
                addToast(err.message || 'Erro ao processar pedido', 'error');
            }
        } catch (err) {
            addToast('Erro técnico ao processar formulário', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        const id = confirmState.id;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/escalaos/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                addToast('Escalão eliminado com sucesso!', 'warning');
                await fetchEscalaos();
            } else {
                addToast('Erro ao eliminar escalão', 'error');
            }
        } catch (err) {
            addToast('Erro ao processar eliminação', 'error');
        } finally {
            setConfirmState({ isOpen: false, id: null, name: '' });
        }
    };

    const handleEdit = (esc) => {
        setEditingEscalao(esc);
        setFormData({
            name: esc.name,
            minAge: esc.minAge || '',
            maxAge: esc.maxAge || '',
            sportIds: esc.sportIds || []
        });
        setShowModal(true);
    };

    const openAddModal = () => {
        setEditingEscalao(null);
        setFormData({
            name: '',
            minAge: '',
            maxAge: '',
            sportIds: []
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingEscalao(null);
    };

    const toggleSport = (id) => {
        setFormData(prev => ({
            ...prev,
            sportIds: prev.sportIds.includes(id)
                ? prev.sportIds.filter(s => s !== id)
                : [...prev.sportIds, id]
        }));
    };

    const filteredEscalaos = escalaos.filter(e => {
        const matchesSearch = e.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesSport = !filterSportId ||
            (e.sportIds && e.sportIds.includes(parseInt(filterSportId)));
        return matchesSearch && matchesSport;
    });

    if (loading) {
        return (
            <div className="escalao-manager loading-state">
                <Loader2 className="spinner" size={48} />
                <p>Sincronizando configurações de escalões...</p>
            </div>
        );
    }

    return (
        <div className="escalao-manager">
            {/* Toasts Container */}
            <div className="toast-container">
                {toasts.map(t => (
                    <Toast key={t.id} {...t} onClose={() => removeToast(t.id)} />
                ))}
            </div>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmState.isOpen}
                title="Eliminar Escalão"
                message={`Deseja eliminar permanentemente o escalão "${confirmState.name}"? Esta ação não pode ser desfeita.`}
                onConfirm={handleDelete}
                onCancel={() => setConfirmState({ isOpen: false, id: null, name: '' })}
            />

            <header className="manager-header">
                <div className="header-content">
                    <h1>Gestão de Escalões</h1>
                    <p className="header-subtitle">Gestão de categorias por idade e modalidades associadas</p>
                </div>
                <button className="btn-add" onClick={openAddModal}>
                    <Plus size={20} /> Novo Escalão
                </button>
            </header>

            <div className="manager-controls glass-card">
                <div className="search-wrapper">
                    <input
                        type="text"
                        placeholder="Pesquisar escalões por nome..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-wrapper">
                    <Filter className="filter-icon" size={18} />
                    <select
                        value={filterSportId}
                        onChange={(e) => setFilterSportId(e.target.value)}
                    >
                        <option value="">Todas as modalidades</option>
                        {sports.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="escalao-list">
                {filteredEscalaos.length === 0 ? (
                    <div className="empty-state glass-card">
                        <Users className="empty-icon" size={64} />
                        <h3>Sem escalões encontrados</h3>
                        <p>Ajuste os filtros ou crie uma nova categoria.</p>
                    </div>
                ) : (
                    filteredEscalaos.map((esc, index) => (
                        <div
                            key={esc.id}
                            className="escalao-row-card animate-in"
                            style={{ '--delay': `${index * 0.05}s` }}
                        >
                            <div className="escalao-info">
                                <div className="escalao-title-area">
                                    <div className="escalao-icon-box">
                                        <Users size={24} />
                                    </div>
                                    <div className="escalao-text-box">
                                        <h3>{esc.name}</h3>
                                        <div className="age-pills">
                                            <span className="age-label">Idades:</span>
                                            <span className="age-value">
                                                {esc.minAge || 0} — {esc.maxAge || '∞'}
                                            </span>
                                            <span className="age-unit">anos</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="escalao-sports-tags">
                                {esc.sportIds && esc.sportIds.length > 0 ? (
                                    esc.sportIds.map(sId => {
                                        const sportName = sports.find(sp => sp.id === sId)?.name || 'Sport';
                                        return <span key={sId} className="sport-pill">{sportName}</span>;
                                    })
                                ) : (
                                    <span className="no-sports">Disponível em todas as modalidades</span>
                                )}
                            </div>

                            <div className="escalao-row-actions">
                                <button className="row-action-btn edit" onClick={() => handleEdit(esc)}>
                                    <Pencil size={18} />
                                    <span>Editar</span>
                                </button>
                                <button className="row-action-btn delete" onClick={() => setConfirmState({ isOpen: true, id: esc.id, name: esc.name })}>
                                    <Trash2 size={18} />
                                </button>
                                <div className="row-chevron">
                                    <ChevronRight size={24} />
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
                    <div className={`modal glass-card ${editingEscalao ? 'modal-editing' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <div className="header-title">
                                <div className="header-icon">
                                    {editingEscalao ? <Pencil size={20} /> : <Plus size={20} />}
                                </div>
                                <div>
                                    <h2>{editingEscalao ? 'Editar Escalão' : 'Novo Escalão'}</h2>
                                    <p className="modal-subtitle">Configure as regras de idade e modalidades</p>
                                </div>
                            </div>
                            <button className="modal-close" onClick={closeModal}><X size={24} /></button>
                        </header>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-grid">
                                <div className="form-column">
                                    <div className="form-section-group">
                                        <label className="section-label">Configuração Básica</label>
                                        <div className="form-group">
                                            <label>Nome do Escalão</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="Ex: Sub-13, Iniciados..."
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                autoFocus
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Intervalo de Idades</label>
                                            <div className="age-range-inputs">
                                                <div className="age-input-field">
                                                    <span>Mín.</span>
                                                    <input
                                                        type="number"
                                                        value={formData.minAge}
                                                        onChange={(e) => setFormData({ ...formData, minAge: e.target.value })}
                                                    />
                                                </div>
                                                <div className="age-separator">—</div>
                                                <div className="age-input-field">
                                                    <span>Máx.</span>
                                                    <input
                                                        type="number"
                                                        value={formData.maxAge}
                                                        onChange={(e) => setFormData({ ...formData, maxAge: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-column">
                                    <div className="form-section-group">
                                        <label className="section-label">Modalidades Associadas</label>
                                        <p className="section-description">Selecione as modalidades onde este escalão é aplicável.</p>
                                        <div className="sports-selection-grid">
                                            {sports.map(s => (
                                                <label
                                                    key={s.id}
                                                    className={`sport-option ${formData.sportIds.includes(s.id) ? 'selected' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.sportIds.includes(s.id)}
                                                        onChange={() => toggleSport(s.id)}
                                                    />
                                                    <span className="checkbox-custom">
                                                        {formData.sportIds.includes(s.id) && <Check size={12} />}
                                                    </span>
                                                    <span className="sport-name">{s.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <footer className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={closeModal}>Cancelar</button>
                                <button type="submit" className="btn-submit" disabled={submitting || !formData.name.trim()}>
                                    {submitting ? <Loader2 className="spinner" size={18} /> : (editingEscalao ? 'Guardar Alterações' : 'Criar Escalão')}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EscalaoManager;
